import React, { lazy, Suspense } from "react";
import { Routes, Route, HashRouter, Navigate, useParams, useLocation } from "react-router-dom";

import "antd/dist/reset.css";
import "./App.scss";

import { Skeleton } from "antd";

import Spinner from "./components/spin/Spin";
import OrgHomeRedirect from "./components/auth/OrgHomeRedirect";
import RequireUserType from "./components/auth/RequireUserType";
import RequireSuperAdmin from "./components/auth/RequireSuperAdmin";
import { USER_TYPES } from "./auth/userTypeUtils";

import AuditReport from "./modules/reports/auditReport/AuditReport";
import AlarmDatewiseReport from "./modules/reports/alarmDatewiseReport/AlarmDatewiseReport";
import DailyStatsReport from "./modules/reports/dailyStatsReport/DailyStatsReport";
// import JSLFeedbackReport from "./modules/reports/jslFeedbackReport/JSLFeedbackReport";
import JSLOverallReport from "./modules/reports/jslOverallReport/JSLOverallReport";
import SiReport from "./modules/reports/siReport/SiReport";

const AuditReportV2       = lazy(() => import("./modules/reports/auditReport/AuditReportV2"));
const AlarmDatewiseReportV2 = lazy(() => import("./modules/reports/alarmDatewiseReport/AlarmDatewiseReportV2"));
const DailyStatsReportV2  = lazy(() => import("./modules/reports/dailyStatsReport/DailyStatsReportV2"));
const JSLOverallReportV2  = lazy(() => import("./modules/reports/jslOverallReport/JSLOverallReportV2"));
const SiReportV2          = lazy(() => import("./modules/reports/siReport/SiReportV2"));

const Login = lazy(() => import("./components/login/Login"));
const SelectDashboard = lazy(() =>
  import("./components/auth/SelectDashboard")
);
const ResetPassword = lazy(() => import("./components/password/ResetPassword"));
const ForgotPassword = lazy(() =>
  import("./components/password/ForgotPassword")
);
const MigrateUsername = lazy(() =>
  import("./components/auth/MigrateUsername")
);

const PageNotFound = lazy(() => import("./components/error404/pageNotFound"));

const LayoutGuest = lazy(() => import("./layout/LayoutGuest"));
const LayoutSecure = lazy(() => import("./layout/LayoutSecure"));

const Dashboard = lazy(() => import("./modules/dashboard/Dashboard"));
const DashboardV2 = lazy(() => import("./modules/dashboard/DashboardV2"));
const FwUpgradeActivityV2 = lazy(() =>
  import("./modules/fwUpgradeActivity/FwUpgradeActivityV2")
);
const SalesMonitoring = lazy(() =>
  import("./modules/salesMonitoring/SalesMonitoring")
);
const SalesMonitoringV2 = lazy(() =>
  import("./modules/salesMonitoring/SalesMonitoringV2")
);
const UsersList = lazy(() => import("./modules/usersList/UsersList"));
const UsersListV2 = lazy(() => import("./modules/usersList/UsersListV2"));
const GatewaysList = lazy(() => import("./modules/gatewaysList/GatewaysList"));
const GatewaysListV2 = lazy(() => import("./modules/gatewaysList/GatewaysListV2"));
const DevicesList = lazy(() => import("./modules/devicesList/DevicesList"));
const DevicesListV2 = lazy(() => import("./modules/devicesList/DevicesListV2"));
const TagAnalytics = lazy(() => import("./modules/tagAnalytics/TagAnalytics"));
const TagAnalyticsV2 = lazy(() => import("./modules/tagAnalytics/TagAnalyticsV2"));
const Analytics = lazy(() => import("./modules/analytics/Analytics"));
const AnalyticsV2 = lazy(() => import("./modules/analytics/AnalyticsV2"));
const AlarmsNotifications = lazy(() =>
  import("./modules/alarmNotifications/AlarmsAndNotifications.jsx")
);
const AlarmNotificationJioPoint = lazy(() =>
  import(
    "./modules/common/alarmNotificationJioPoint/AlarmNotificationJioPoint.jsx"
  )
);
const AlarmNotificationDetails = lazy(() =>
  import(
    "./modules/common/alarmNotificationDetail/AlarmNotificationDetails.jsx"
  )
);
const JioSmartMonitoring = lazy(() =>
  import("./modules/jioSmartMonitoring/JioSmartMonitoring")
);
const JioSmartMonitoringV2 = lazy(() =>
  import("./modules/jioSmartMonitoring/JioSmartMonitoringV2")
);
const BrandwiseDevices = lazy(() =>
  import("./modules/brandWiseDevices/BrandwiseDevices")
);
const BrandwiseDevicesV2 = lazy(() =>
  import("./modules/brandWiseDevices/BrandwiseDevicesV2")
);

const MonitoringSummary = lazy(() =>
  import("./modules/monitoringSummary/MonitoringSummary.jsx")
);
const MonitoringSummaryV2 = lazy(() =>
  import("./modules/monitoringSummary/MonitoringSummaryV2.jsx")
);
const GeoPulse = lazy(() => import("./modules/geoPulse/GeoPulse"));
const Interests = lazy(() => import("./modules/interests/Interests.jsx"));
const InterestsV2 = lazy(() => import("./modules/interests/InterestsV2.jsx"));
const UsersManagement = lazy(() =>
  import("./modules/usersManagement/UsersManagement")
);
const UsersManagementV2 = lazy(() =>
  import("./modules/usersManagement/UsersManagementV2")
);
const VirtualGateway = lazy(() => import("./modules/vgw/VirtualGateway"));

// const CameraList = lazy(() =>
//   import("./modules/jioSmartMonitoring/CameraList")
// );

const CameraList = lazy(() =>
  import("./modules/common/cameraList/CameraList.jsx")
);

const RecAnalytics = lazy(() =>
  import("./modules/recommendation/RecAnalytics")
);
const RecAnalyticsV2 = lazy(() =>
  import("./modules/recommendation/RecAnalyticsV2")
);

const DevicesView = lazy(() => import("./modules/common/devicesView/Devices"));
const UserView = lazy(() => import("./modules/common/usersView/User"));

const SasDetailsView = lazy(() =>
  import("./modules/common/sasDetailsView/SasDetailsView.jsx")
);

const JioPointDashboard = lazy(() =>
  import("./modules/jiopoint/dashboard/JioPointDashboard")
);
const JioPointGatewaysList = lazy(() =>
  import("./modules/jiopoint/gatewaysList/JioPointGatewaysList")
);
const JioPointUsersList = lazy(() =>
  import("./modules/jiopoint/usersList/JioPointUsersList")
);
const JioPointUserView = lazy(() =>
  import("./modules/jiopoint/usersView/User")
);
const JioPointAlarmsAndNotifications = lazy(() =>
  import("./modules/jiopoint/alarmNotifications/JioPointAlarmsAndNotifications.jsx")
);
const JioPointSurveillance = lazy(() =>
  import("./modules/jiopoint/surveillance/JioPointSurveillance")
);
const JioPointNvrList = lazy(() =>
  import("./modules/jiopoint/surveillance/JioPointNvrList")
);
const JioPointCameraList = lazy(() =>
  import("./modules/jiopoint/surveillance/JioPointCameraList")
);
const JioPointAlarmDetailsView = lazy(() =>
  import("./modules/jiopoint/alarmNotifications/JioPointAlarmDetailsView.jsx")
);const JioPointList = lazy(() =>
  import("./modules/jiopoint/jiopointList/JioPointList")
);
const JioPointDetail = lazy(() =>
  import("./modules/jiopoint/jiopointDetail/JioPointDetail")
);
const JioPointReports = lazy(() =>
  import("./modules/jiopoint/reports/JioPointReports")
);
const JioPointAlarmNotificationJioPoint = lazy(() =>
  import("./modules/jiopoint/alarmNotificationJioPoint/AlarmNotificationJioPoint.jsx")
);
const JioPointAlarmNotificationGatewayWiseDetails = lazy(() =>
  import("./modules/jiopoint/alarmNotificationJioPoint/details/AlarmNotificationGatewayWiseDetails.jsx")
);
const JioPointMap = lazy(() =>
  import("./modules/jiopoint/map/JioPointMap")
);
const SystemUpV2 = lazy(() =>
  import("./modules/systemUp/SystemUpV2")
);
const BrokerMonitoring = lazy(() =>
  import("./modules/brokerMonitoring/BrokerMonitoring")
);
const WiseDevices = lazy(() =>
  import("./modules/brokerMonitoring/pages/WiseDevices")
);
const GatewayDevices = lazy(() =>
  import("./modules/brokerMonitoring/pages/GatewayDevices")
);
const SystemUpPublic = lazy(() =>
  import("./modules/systemUp/public/SystemUpPublic")
);

/** Redirect legacy `/gateways/:tabKey` to v2, preserving query string. */
function RedirectGatewaysToV2() {
  const { tabKey } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/gateways-v2/${tabKey}${search}`} replace />;
}

const App = () => (
  <HashRouter>
    <Routes>
      <Route
        path="/"
        element={
          <Suspense fallback={<Spinner />}>
            <LayoutGuest />
          </Suspense>
        }
      >
        <Route index element={<Login />} />
        <Route path="login" element={<Login />} />
        <Route path="select-dashboard" element={<SelectDashboard />} />
        <Route path="forget-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route
          path="migrate-username"
          element={
            <Suspense fallback={<Spinner />}>
              <MigrateUsername />
            </Suspense>
          }
        />
        <Route
          path="systemup"
          element={
            <Suspense fallback={<Spinner />}>
              <SystemUpPublic />
            </Suspense>
          }
        />
      </Route>

      <Route
        path="/"
        element={
          <Suspense
            fallback={
              <div className="flex min-h-screen gap-4 bg-canvas p-4">
                <Skeleton.Node
                  active
                  className="w-[72px] shrink-0 min-h-[calc(100vh-2rem)]"
                />
                <div className="min-w-0 flex-1">
                  <Skeleton active paragraph={{ rows: 1 }} title={{ width: "60%" }} />
                  <Skeleton active paragraph={{ rows: 12 }} title={false} />
                </div>
              </div>
            }
          >
            <LayoutSecure />
          </Suspense>
        }
      >
        <Route index element={<OrgHomeRedirect />} />
        <Route path="jiopoint/dashboard" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointDashboard /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/gateways" element={<Navigate to="/jiopoint/gateways/1" replace />} />
        <Route path="jiopoint/gateways/:tabKey" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointGatewaysList /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/users-list" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointUsersList /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/users-view" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointUserView /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/alarms-and-notifications" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointAlarmsAndNotifications /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/surveillance/nvr-list" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointNvrList /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/surveillance/camera-list" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointCameraList /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/surveillance" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointSurveillance /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/alarm-details" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointAlarmDetailsView /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/alarm-notification-gateway-wise" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointAlarmNotificationJioPoint /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/alarm-notification-gateway-wise-details" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointAlarmNotificationGatewayWiseDetails /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/jiopoints-list" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointList /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/map" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointMap /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/jiopoint-detail" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointDetail /></Suspense>
          </RequireUserType>
        } />
        <Route path="jiopoint/reports" element={
          <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
            <Suspense fallback={<Spinner />}><JioPointReports /></Suspense>
          </RequireUserType>
        } />
        <Route
          path="dashboard"
          element={<Navigate to="/dashboard-v2" replace />}
        />
        <Route path="dashboard-classic" element={<Dashboard />} />
        <Route path="dashboard-v2" element={
          <RequireUserType allowed={[USER_TYPES.DAM]}>
            <Suspense fallback={<Spinner />}><DashboardV2 /></Suspense>
          </RequireUserType>
        } />
        <Route path="fw-upgrade-activity" element={
          <RequireUserType allowed={[USER_TYPES.DAM]}>
            <Suspense fallback={<Spinner />}><FwUpgradeActivityV2 /></Suspense>
          </RequireUserType>
        } />
        <Route
          path="sales-monitoring"
          element={<Navigate to="/sales-monitoring-v2" replace />}
        />
        <Route
          path="sales-monitoring-classic"
          element={<SalesMonitoring />}
        />
        <Route path="sales-monitoring-v2" element={<SalesMonitoringV2 />} />
        <Route path="devices-view" element={<DevicesView />} />
        <Route
          path="users-list"
          element={<Navigate to="/users-list-v2" replace />}
        />
        <Route path="users-list-classic" element={<UsersList />} />
        <Route path="users-list-v2" element={<UsersListV2 />} />
        <Route path="users-view" element={<UserView />} />
        <Route path="gateways/:tabKey" element={<RedirectGatewaysToV2 />} />
        <Route path="gateways-classic/:tabKey" element={<GatewaysList />} />
        <Route path="gateways-v2" element={<Navigate to="/gateways-v2/1" replace />} />
        <Route path="gateways-v2/:tabKey" element={<GatewaysListV2 />} />
        <Route
          path="devices-list"
          element={<Navigate to="/devices-list-v2" replace />}
        />
        <Route path="devices-list-classic" element={<DevicesList />} />
        <Route path="devices-list-v2" element={<DevicesListV2 />} />
        <Route
          path="tag-analytics"
          element={<Navigate to="/tag-analytics-v2" replace />}
        />
        <Route path="tag-analytics-classic" element={<TagAnalytics />} />
        <Route path="tag-analytics-v2" element={<TagAnalyticsV2 />} />
        <Route path="analytics" element={<AnalyticsV2 />} />
        <Route path="analytics-classic" element={<Analytics />} />
        <Route path="jio-surveillance-camera-list" element={<CameraList />} />
        <Route path="rec-analytics" element={<RecAnalyticsV2 />} />
        <Route path="rec-analytics-v2" element={<RecAnalyticsV2 />} />
        <Route path="rec-analytics-classic" element={<RecAnalytics />} />
        <Route
          path="alarms-and-notifications"
          element={<AlarmsNotifications />}
        />
        <Route path="sas-details-view" element={<SasDetailsView />} />
        <Route
          path="alarm-notification-jio-point"
          element={
            <RequireUserType allowed={[USER_TYPES.JIOPOINT]}>
              <Suspense fallback={<Spinner />}><AlarmNotificationJioPoint /></Suspense>
            </RequireUserType>
          }
        />
        <Route
          path="alarm-notification-details"
          element={<AlarmNotificationDetails />}
        />
        <Route
          path="jio-smart-monitoring"
          element={<Navigate to="/jio-smart-monitoring-v2" replace />}
        />
        <Route
          path="jio-smart-monitoring-classic"
          element={<JioSmartMonitoring />}
        />
        <Route path="jio-smart-monitoring-v2" element={<JioSmartMonitoringV2 />} />
        <Route
          path="brand-wise-devices"
          element={<Navigate to="/brand-wise-devices-v2" replace />}
        />
        <Route
          path="brand-wise-devices-classic"
          element={<BrandwiseDevices />}
        />
        <Route path="brand-wise-devices-v2" element={<BrandwiseDevicesV2 />} />
        <Route path="geo-pulse" element={
          <RequireUserType allowed={[USER_TYPES.DAM]}>
            <Suspense fallback={<Spinner />}><GeoPulse /></Suspense>
          </RequireUserType>
        } />
        <Route
          path="monitoring-summary"
          element={<Navigate to="/monitoring-summary-v2" replace />}
        />
        <Route
          path="monitoring-summary-classic"
          element={<MonitoringSummary />}
        />
        <Route path="monitoring-summary-v2" element={<MonitoringSummaryV2 />} />
        <Route path="system-up-v2" element={
          <RequireUserType allowed={[USER_TYPES.DAM]}>
            <Suspense fallback={<Spinner />}><SystemUpV2 /></Suspense>
          </RequireUserType>
        } />
        <Route path="broker-monitoring" element={
          <RequireSuperAdmin>
            <Suspense fallback={<Spinner />}><BrokerMonitoring /></Suspense>
          </RequireSuperAdmin>
        } />
        <Route path="broker-monitoring/wise" element={
          <RequireSuperAdmin>
            <Suspense fallback={<Spinner />}><WiseDevices /></Suspense>
          </RequireSuperAdmin>
        } />
        <Route path="broker-monitoring/gateway" element={
          <RequireSuperAdmin>
            <Suspense fallback={<Spinner />}><GatewayDevices /></Suspense>
          </RequireSuperAdmin>
        } />
        <Route
          path="interests"
          element={<Navigate to="/interests-v2" replace />}
        />
        <Route path="interests-classic" element={<Interests />} />
        <Route path="interests-v2" element={<InterestsV2 />} />
        <Route path="virtual-gateway" element={<VirtualGateway />} />
        <Route
          path="users-management"
          element={<Navigate to="/users-management-v2" replace />}
        />
        <Route
          path="users-management-classic"
          element={<UsersManagement />}
        />
        <Route path="users-management-v2" element={<UsersManagementV2 />} />
        <Route
          path="audit-report"
          element={<Navigate to="/audit-report-v2" replace />}
        />
        <Route path="audit-report-classic" element={<AuditReport />} />
        <Route path="audit-report-v2" element={<AuditReportV2 />} />
        <Route
          path="alarm-date-wise-report"
          element={<Navigate to="/alarm-date-wise-report-v2" replace />}
        />
        <Route
          path="alarm-date-wise-report-classic"
          element={<AlarmDatewiseReport />}
        />
        <Route path="alarm-date-wise-report-v2" element={<AlarmDatewiseReportV2 />} />
        <Route
          path="daily-stats-report"
          element={<Navigate to="/daily-stats-report-v2" replace />}
        />
        <Route path="daily-stats-report-classic" element={<DailyStatsReport />} />
        <Route path="daily-stats-report-v2" element={<DailyStatsReportV2 />} />
        {/* <Route path="jsl-feedback-report" element={<JSLFeedbackReport />} /> */}
        <Route
          path="jsl-overall-report"
          element={<Navigate to="/jsl-overall-report-v2" replace />}
        />
        <Route path="jsl-overall-report-classic" element={<JSLOverallReport />} />
        <Route path="jsl-overall-report-v2" element={<JSLOverallReportV2 />} />
        <Route
          path="si-report"
          element={<Navigate to="/si-report-v2" replace />}
        />
        <Route path="si-report-classic" element={<SiReport />} />
        <Route path="si-report-v2" element={<SiReportV2 />} />
      </Route>
      <Route path="*" element={<PageNotFound />}></Route>
    </Routes>
  </HashRouter>
);

export default App;